import { useEffect } from 'react';
import SeoJsonLd from '../../components/feature/SeoJsonLd';

export default function PrivacyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy - Caption King';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Read Caption King\'s privacy policy to understand how we collect, use, and protect your personal information and photo data.');
    }
  }, []);

  const seoData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Privacy Policy',
    description: 'Privacy policy for Caption King - AI Instagram Caption Generator',
    url: 'https://tryaikit.com/privacy',
    publisher: {
      '@type': 'Organization',
      name: 'Caption King',
    },
  };

  return (
    <>
      <SeoJsonLd data={seoData} />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white">
        {/* Navigation */}
        <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between">
              <a href="/" className="flex items-center gap-2">
                <img src="https://public.readdy.ai/ai/img_res/15ec66b2-609e-40f0-bdad-7a5a43050de3.png" alt="Caption King" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
                <span className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Caption King
                </span>
              </a>
              <a 
                href="/"
                className="px-3 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs sm:text-base rounded-full font-semibold hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap"
              >
                Back to Home
              </a>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-16">
          <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-3 sm:mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mb-8 sm:mb-12">Last updated: January 15, 2025</p>
          
          <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl p-4 sm:p-8 space-y-6 sm:space-y-8">
            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">1. Introduction</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Welcome to Caption King ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered Instagram caption generation service.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">2. Information We Collect</h2>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Personal Information</h3>
                  <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                    <li>Email address (for account creation and communication)</li>
                    <li>First name (optional, for personalization)</li>
                    <li>Payment information (processed securely through third-party payment processors)</li>
                    <li>Usage data and preferences</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Content You Provide</h3>
                  <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                    <li>Photos you upload for caption generation</li>
                    <li>Saved favorite captions</li>
                    <li>Feedback and communications with us</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">Automatically Collected Information</h3>
                  <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                    <li>Device information (browser type, operating system)</li>
                    <li>IP address and location data</li>
                    <li>Cookies and similar tracking technologies</li>
                    <li>Usage statistics and analytics</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">3. How We Use Your Information</h2>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                <li>To provide and maintain our caption generation service</li>
                <li>To process your payments and manage subscriptions</li>
                <li>To improve our AI algorithms and service quality</li>
                <li>To send you updates, newsletters, and promotional materials (with your consent)</li>
                <li>To respond to your inquiries and provide customer support</li>
                <li>To detect, prevent, and address technical issues or fraudulent activity</li>
                <li>To comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">4. Data Retention</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy. Uploaded photos are processed immediately and deleted from our servers within 24 hours unless you save them to your favorites. Account data is retained until you request deletion.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-2 sm:mb-3">
                We do not sell your personal information. We may share your information with:
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                <li><strong>Service Providers:</strong> Third-party companies that help us operate our service (payment processors, hosting providers, analytics services)</li>
                <li><strong>AI Processing:</strong> Your photos are processed by our AI partners to generate captions</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">6. Your Rights (GDPR & CCPA)</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-2 sm:mb-3">
                Depending on your location, you have the following rights:
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
                <li><strong>Restriction:</strong> Limit how we use your data</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Objection:</strong> Object to processing of your data</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mt-2 sm:mt-3">
                To exercise these rights, contact us at <a href="mailto:hello@tryaikit.com" className="text-purple-600 hover:text-purple-700 font-semibold">hello@tryaikit.com</a>
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">7. Cookies and Tracking</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-2 sm:mb-3">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside text-sm sm:text-base text-gray-700 space-y-1.5 sm:space-y-2 ml-2 sm:ml-4">
                <li>Remember your preferences and settings</li>
                <li>Analyze site traffic and usage patterns</li>
                <li>Provide personalized content and advertisements</li>
                <li>Improve security and prevent fraud</li>
              </ul>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mt-2 sm:mt-3">
                You can control cookies through your browser settings. Note that disabling cookies may affect functionality.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">8. International Data Transfers</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with GDPR and other applicable laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">9. Security</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                We implement industry-standard security measures to protect your personal information, including encryption, secure servers, and regular security audits. However, no method of transmission over the internet is 100% secure.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">10. Children's Privacy</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Our service is not intended for users under 16 years of age. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">11. Changes to This Policy</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a notice on our website. Your continued use of the service after changes constitutes acceptance.
              </p>
            </section>

            <section>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">12. Contact Us</h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                If you have questions about this Privacy Policy or wish to exercise your rights, contact us:
              </p>
              <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-purple-50 rounded-lg">
                <p className="text-sm sm:text-base text-gray-800"><strong>Email:</strong> <a href="mailto:hello@tryaikit.com" className="text-purple-600 hover:text-purple-700">hello@tryaikit.com</a></p>
              </div>
            </section>
          </div>

          <div className="mt-8 sm:mt-12 text-center">
            <a 
              href="/"
              className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 font-semibold text-base sm:text-lg"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-8 sm:py-12 mt-12 sm:mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
              <div className="flex items-center gap-2">
                <img src="https://public.readdy.ai/ai/img_res/15ec66b2-609e-40f0-bdad-7a5a43050de3.png" alt="Caption King" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
                <span className="text-lg sm:text-2xl font-bold">Caption King</span>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm">
                <a href="/privacy" className="hover:text-purple-400 transition-colors">Privacy Policy</a>
                <a href="/terms" className="hover:text-purple-400 transition-colors">Terms of Service</a>
                <a href="mailto:hello@tryaikit.com" className="hover:text-purple-400 transition-colors">hello@tryaikit.com</a>
              </div>
            </div>
            
            <div className="text-center text-gray-400 text-xs sm:text-sm mt-6 sm:mt-8">
              © 2025 TryAIKit. All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
